/* ============================================================
 * BEGIN CUSTOM EDIT: Editorial Banner AJAX Collection Swap
 * Date: 2026-04-15
 * What: Prefetch-on-hover + soft-navigate between collections triggered
 *       by editorial banner thumbnail clicks. Replaces <main#main> contents,
 *       wraps in View Transitions when available, emits dataLayer events for
 *       GTM / Stape. Gated per-grid via [data-ajax="true"].
 * ============================================================ */
(function () {
  if (typeof window === 'undefined') return;
  if (window.Shopify && window.Shopify.designMode === true) return;

  var SWAP_SELECTORS = ['.cbe-forms-grid[data-ajax="true"]'];

  // In-memory cache. We store the in-flight Promise so a click during prefetch
  // dedups to the same request, and the resolved Response replaces the Promise
  // so later callers clone directly without waiting.
  var htmlCache = new Map();   // url -> Promise<Response> | Response
  function primeCache(url) {
    if (htmlCache.has(url)) return;
    var p = fetch(url).then(function (res) {
      if (res.ok) htmlCache.set(url, res.clone());   // swap Promise for Response
      return res;
    }).catch(function (e) {
      htmlCache.delete(url);
      throw e;
    });
    htmlCache.set(url, p);
  }
  function cacheLookup(url) {
    var v = htmlCache.get(url);
    if (!v) return null;
    // If it's a Response, clone it synchronously
    if (v.clone) return Promise.resolve(v.clone());
    // Otherwise it's a Promise<Response> still in flight — await + clone
    return v.then(function (res) { return res.clone(); });
  }

  function parseCollectionHandle(url) {
    try {
      var u = new URL(url, location.origin);
      var m = u.pathname.match(/\/collections\/([^\/?#]+)/);
      return m ? m[1] : null;
    } catch (e) { return null; }
  }

  function stripFilterParams(url) {
    try {
      var u = new URL(url, location.origin);
      var keep = new URLSearchParams();
      u.searchParams.forEach(function (value, key) {
        if (!key.startsWith('filter.') && key !== 'page') keep.append(key, value);
      });
      u.search = keep.toString();
      return u.toString();
    } catch (e) { return url; }
  }

  function isInternal(href) {
    try { return new URL(href, location.origin).origin === location.origin; }
    catch (e) { return false; }
  }

  function dispatchPrefetch(url, trigger) {
    document.dispatchEvent(new CustomEvent('cbe:collection-prefetch', {
      detail: {
        url: url,
        handle: parseCollectionHandle(url),
        trigger: trigger,
        source: 'editorial_banner_thumbnails'
      }
    }));
  }

  function dispatchSwap(detail) {
    document.dispatchEvent(new CustomEvent('cbe:collection-swap', { detail: detail }));
  }

  function updateMeta(doc) {
    [
      ['link[rel="canonical"]', 'href'],
      ['meta[property="og:url"]', 'content'],
      ['meta[property="og:title"]', 'content'],
      ['meta[property="og:description"]', 'content'],
      ['meta[name="description"]', 'content'],
      ['meta[name="twitter:title"]', 'content'],
      ['meta[name="twitter:description"]', 'content']
    ].forEach(function (pair) {
      var curr = document.head.querySelector(pair[0]);
      var next = doc.head.querySelector(pair[0]);
      if (curr && next) curr.setAttribute(pair[1], next.getAttribute(pair[1]));
    });
  }

  function CbeCollectionSwap(grid) {
    this.grid = grid;
    this.prefetched = new Set();
    this.abortController = null;
    this.activeLink = null;
    this._bind();
    this._eagerPrefetch();
  }

  // Prefetch every thumbnail URL once the page is fully idle. Runs once per grid
  // after the browser has finished the initial render so we don't fight for bandwidth.
  CbeCollectionSwap.prototype._eagerPrefetch = function () {
    var self = this;
    var run = function () {
      self.grid.querySelectorAll('.cbe-forms-grid__link[href]').forEach(function (a) {
        var href = stripFilterParams(a.href);
        if (!isInternal(href) || self.prefetched.has(href)) return;
        self.prefetched.add(href);
        primeCache(href);
        dispatchPrefetch(href, 'eager');
      });
    };
    var schedule = function () {
      if ('requestIdleCallback' in window) {
        requestIdleCallback(run, { timeout: 2500 });
      } else {
        setTimeout(run, 1200);
      }
    };
    if (document.readyState === 'complete') schedule();
    else window.addEventListener('load', schedule, { once: true });
  };

  CbeCollectionSwap.prototype._bind = function () {
    var self = this;
    this.grid.addEventListener('mouseenter', function (e) { self._onPrefetchTrigger(e, 'mouseenter'); }, { capture: true, passive: true });
    this.grid.addEventListener('focusin',   function (e) { self._onPrefetchTrigger(e, 'focusin'); }, { passive: true });
    this.grid.addEventListener('touchstart',function (e) { self._onPrefetchTrigger(e, 'touchstart'); }, { capture: true, passive: true });
    this.grid.addEventListener('click', function (e) { self._onClick(e); });
  };

  CbeCollectionSwap.prototype._onPrefetchTrigger = function (e, trigger) {
    var a = e.target && e.target.closest ? e.target.closest('.cbe-forms-grid__link[href]') : null;
    if (!a) return;
    var href = stripFilterParams(a.href);
    if (!isInternal(href)) return;
    if (this.prefetched.has(href)) return;
    this.prefetched.add(href);
    primeCache(href);
    dispatchPrefetch(href, trigger);
  };

  CbeCollectionSwap.prototype._onClick = function (e) {
    if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey || e.button !== 0) return;
    var a = e.target && e.target.closest ? e.target.closest('.cbe-forms-grid__link[href]') : null;
    if (!a) return;
    if (!isInternal(a.href)) return;
    e.preventDefault();
    var href = stripFilterParams(a.href);
    this._setActiveLink(a);
    this._moveCurrentTo(a);
    this._softNavigate(href);
  };

  // Move .is-current onto the clicked thumb BEFORE the view transition captures
  // snapshots so the frozen thumbnails grid already shows the new active outline.
  CbeCollectionSwap.prototype._moveCurrentTo = function (clickedLink) {
    var item = clickedLink.closest('.cbe-forms-grid__item');
    if (!item) return;
    this.grid.querySelectorAll('.cbe-forms-grid__item.is-current').forEach(function (el) {
      if (el !== item) el.classList.remove('is-current');
    });
    item.classList.add('is-current');
    clickedLink.setAttribute('aria-current', 'true');
  };

  CbeCollectionSwap.prototype._setActiveLink = function (link) {
    if (this.activeLink) this.activeLink.classList.remove('cbe-is-loading');
    this.activeLink = link;
    if (link) link.classList.add('cbe-is-loading');
  };

  CbeCollectionSwap.prototype._clearActiveLink = function () {
    if (this.activeLink) this.activeLink.classList.remove('cbe-is-loading');
    this.activeLink = null;
  };

  CbeCollectionSwap.prototype._softNavigate = function (url, opts) {
    var self = this;
    opts = opts || {};
    if (this.abortController) this.abortController.abort();
    this.abortController = new AbortController();

    var fromHandle = parseCollectionHandle(location.href);
    var toHandle   = parseCollectionHandle(url);
    var startAt    = performance.now();

    document.documentElement.classList.add('cbe-is-navigating');

    // Prefer the cached Response (primed during prefetch or still-in-flight Promise);
    // fall back to fresh fetch with abort support.
    var cacheHit = htmlCache.has(url);
    var cached = cacheLookup(url);
    var responsePromise = cached || fetch(url, { signal: this.abortController.signal }).then(function (res) {
      if (res.ok) htmlCache.set(url, res.clone());
      return res;
    });
    return responsePromise
      .then(function (res) { return res.text(); })
      .then(function (html) {
        var doc    = new DOMParser().parseFromString(html, 'text/html');
        var target = doc.getElementById('main');
        var curr   = document.getElementById('main');
        if (!target || !curr) throw new Error('main element missing');

        // Preserve the editorial-banner thumbnails grid DOM node across swaps. Because
        // _moveCurrentTo already updated .is-current on the clicked thumb, keeping the
        // same element means the view transition NEVER snapshots the grid (identical
        // element in both states) — outline stays visually pinned to the new active thumb
        // with zero lag or crossfade flicker.
        var currGrid = curr.querySelector('.cbe-forms-grid');

        var swap = function () {
          var newChildren = document.importNode(target, true).childNodes;
          curr.replaceChildren.apply(curr, newChildren);
          // Swap the freshly-inserted grid for the preserved one
          if (currGrid) {
            var incomingGrid = curr.querySelector('.cbe-forms-grid');
            if (incomingGrid) incomingGrid.replaceWith(currGrid);
          }
          history[opts.replace ? 'replaceState' : 'pushState']({ cbeSwap: true, cbeHandle: toHandle }, '', url);
          document.title = doc.title;
          document.documentElement.dataset.cbeHasSwapped = 'true';
          updateMeta(doc);
        };

        if (document.startViewTransition) {
          return document.startViewTransition(swap).finished.catch(function () {});
        } else {
          swap();
          return Promise.resolve();
        }
      })
      .then(function () {
        var duration = performance.now() - startAt;
        window.scrollTo({ top: 0, behavior: 'instant' in window ? 'instant' : 'auto' });
        dispatchSwap({
          url: url,
          from_collection: fromHandle,
          to_collection:   toHandle,
          duration_ms:     Math.round(duration),
          from_cache:      cacheHit,
          source:          'editorial_banner_thumbnails',
          replace:         !!opts.replace
        });
        // Reinit any swap-bound components inside the new DOM
        initAll();
      })
      .catch(function (err) {
        if (err && err.name === 'AbortError') return;
        window.location.assign(url);
      })
      .finally(function () {
        document.documentElement.classList.remove('cbe-is-navigating');
        self._clearActiveLink();
      });
  };

  var currentInstances = new WeakSet();
  function initAll() {
    SWAP_SELECTORS.forEach(function (sel) {
      document.querySelectorAll(sel).forEach(function (grid) {
        if (currentInstances.has(grid)) return;
        currentInstances.add(grid);
        new CbeCollectionSwap(grid);
      });
    });
  }

  // popstate → reuse softNavigate so back/forward feel identical to forward nav
  var popstateBound = false;
  function bindPopstate() {
    if (popstateBound) return;
    popstateBound = true;
    window.addEventListener('popstate', function (e) {
      if (!document.documentElement.dataset.cbeHasSwapped) return;
      // Reuse the first available instance's _softNavigate
      var grid = document.querySelector(SWAP_SELECTORS.join(','));
      if (!grid) { window.location.reload(); return; }
      var inst = new CbeCollectionSwap(grid);
      inst._softNavigate(location.href, { replace: true });
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { initAll(); bindPopstate(); });
  } else {
    initAll();
    bindPopstate();
  }
})();
/* ============================================================
 * END CUSTOM EDIT
 * ============================================================ */
